import React, {useEffect, useMemo, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {UserApiCalls} from "../../apis/UserApiCalls";
import {useLocalStorageNew} from "../../hooks/useLocalStorageNew";
import {
    ACTIVITY_EDITOR_VIEW,
    GRADE_SCHEMA,
    MENU_LIST,
    POLL_TITLE_LENGTH,
    QUESTION_TYPES,
} from "../../utils/constants";
import {
    getDefaultUserSettings,
    notifyError,
    notifySuccess, removePrefixNew,
    transferUserSettings,
    validateText
} from "../../utils/helpers";
import YuJaGeneralAlert from "../../components/modals/YuJaGeneralAlert";
import {
    FormatDate, setBlankRestriction,
    trimLineBreakerInQuestion,
    trimQuestion
} from "../../utils/questionUtils";
import { useTranslation } from "react-i18next";
import "../CreateQuizPage/styles.css";
import {useImageLoaderManager} from "../../hooks/useImageLoaderManager";
import useNavigationGuard from "../../hooks/useNavigationGuard";
import classes from "../CreateQuizPage/ActivityEditor.module.css";
import ActivityEditorTitleContainer from "../CreateQuizPage/ActivityEditorTitleContainer";
import ActivityEditorDashboard from "../CreateQuizPage/ActivityEditorDashboard";
import ActivityEditorMainView from "../CreateQuizPage/ActivityEditorMainView";
import ActivityEditorImportQuestions from "../CreateQuizPage/ActivityEditorImportQuestions";
import {CourseApiCalls} from "../../apis/CourseApiCalls";

export default function EditQuestionProfilePage({setPageIdentifier}) {
    const { t } = useTranslation();
    const {questionProfileId} = useParams();
    const [isSaving, setIsSaving] = useState(false);
    const [userSettings, setUserSettings] = useState(getDefaultUserSettings());
    const [currQueDisplay, setCurrQueDisplay] = useState(1);
    const {updateQuestionProfile, getQuestionProfile, deleteQuestionProfile} = UserApiCalls();
    const {getCourseByUser} = CourseApiCalls();
    const history = useHistory();
    const [, getSession] = useLocalStorageNew("session", {});
    const session = getSession();

    const [setSaveExit, getSaveExit] = useLocalStorageNew("saveExit", false);

    const [isAutoSaving, setIsAutoSaving] = useState(null);
    const [dataLoading, setDataLoading] = useState(true);

    const [savedActivity, setSavedActivity] = useState({});
    const [draftActivity, setDraftActivity] = useState({});
    const [editorView, setEditorView] = useState(ACTIVITY_EDITOR_VIEW.QUESTION_EDITING);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [courseList, setCourseList] = useState([]);


    const loadCourse = async () => {
        const {idpType="", intType=""} = session;
        const courses = await getCourseByUser(idpType, intType);
        const options = courses.map(course => ({
            value: course.courseId,
            label: course.courseName,
            code: course.courseCode,
            isLmsCourse: !!course.index3Pk,
            lmsCourseId: removePrefixNew(!!course.index3Pk ? course.index3Pk : "")
        }));
        setCourseList(options);

        return options;
    }

    const setQuestions = (questions) => {
        const copy = JSON.parse(JSON.stringify(draftActivity));
        copy.questions = questions;
        setDraftActivity(copy);
    }


    const {checkAnyQuestionIsLoading} = useImageLoaderManager(draftActivity.questions, setQuestions);

    const hasUnsavedChanges = useMemo(
        () => JSON.stringify(draftActivity) !== JSON.stringify(savedActivity) && getSaveExit() !== true,
        [draftActivity, getSaveExit, savedActivity],
    );

    const {
        showConfirm: alertShow,
        confirmationModal: leaveConfirmationModal,
    } = useNavigationGuard({
        hasUnsavedChanges,
        onBypass: () => {
            setSaveExit(false);
        },
    });

    const editQueBank = async (updatedQuestions = []) => {
        let defaultTitle = generateDefaultTitle();
        let pollToAdd = {
            questionProfileId: questionProfileId,
            title: draftActivity.pollTitle,
            questions: draftActivity.questions,
        };

        let questionsCopy = updatedQuestions && updatedQuestions.length > 0 ? JSON.parse(JSON.stringify(updatedQuestions)) : pollToAdd.questions;
        pollToAdd.title = !!pollToAdd.title ? pollToAdd.title : defaultTitle;
        for (const que of questionsCopy) {
            trimLineBreakerInQuestion(que);
        }
        pollToAdd.questions = trimQuestion(questionsCopy);

        await updateQuestionProfile(pollToAdd);

        window.onbeforeunload = undefined;
        let copy = JSON.parse(JSON.stringify(draftActivity));
        if (pollToAdd.title !== draftActivity.pollTitle
            || (updatedQuestions && updatedQuestions.length > 0)
        ) {

            if (pollToAdd.title !== draftActivity.pollTitle) {
                copy.pollTitle = pollToAdd.title;
            }

            if (updatedQuestions && updatedQuestions.length > 0) {
                copy.questions = updatedQuestions;
            }

            setDraftActivity(JSON.parse(JSON.stringify(copy)));
            setSavedActivity(copy);
        } else {
            setSavedActivity(copy);
        }
        return true;

    };

    const editQueBankClose = async () => {
        if (!validateText(draftActivity.pollTitle, POLL_TITLE_LENGTH)) {
            notifyError(t("questionBank.nameError"));
            return;
        }


        let pollToAdd = {
            questionProfileId: questionProfileId,
            title: draftActivity.pollTitle,
            questions: draftActivity.questions,
        };
        let questionsCopy = pollToAdd.questions;

        for (const que of questionsCopy) {
            trimLineBreakerInQuestion(que);
        }
        pollToAdd.questions = trimQuestion(questionsCopy);

        setIsSaving(true);
        updateQuestionProfile(pollToAdd).then(res => {
            window.onbeforeunload = undefined;
            setSavedActivity(pollToAdd);
            setDraftActivity(pollToAdd);
            notifySuccess(t("questionBank.saveSuccess", {profileTitle: pollToAdd.title}));
            history.push(MENU_LIST.QUESTION_BANKS.path);
        }) .finally(() => setIsSaving(false));
    };

    const generateDefaultTitle = () => {
        return t("questionBank.title") + ": " + FormatDate(new Date());
    }


    useEffect(() => {
        if (!!alertShow || !!isSaving || !!showDeleteModal) {
            return;
        }

        if (checkAnyQuestionIsLoading()) {
            return;
        }

        const interval = setInterval(() => {
            if (JSON.stringify(draftActivity) !== JSON.stringify(savedActivity)) {
                setIsAutoSaving(true);
                editQueBank().finally(() => {
                    setIsAutoSaving(false);
                });
            }
        }, 10000);

        return () => {
            if (!!interval) {
                clearInterval(interval);
            }
        }
    }, [draftActivity, savedActivity, alertShow, showDeleteModal, isSaving]);

    useEffect(() => {
        setPageIdentifier(t("questionBank.editTitle"));
        setDataLoading(true)
        let {settings = {}} = session;
        settings = transferUserSettings(settings);
        setUserSettings(settings);


        const promise1 = getQuestionProfile(questionProfileId);
        const promise2 =  loadCourse();
        Promise.all([promise1, promise2]).then(([res, ]) => {
            for (const question of res.questions) {
                if (question.questionType === QUESTION_TYPES.MH.name && !question.correctAnswers) {
                    question.correctAnswers = question.optionsMap;
                }
            }

            let activity = {};
            activity.pollTitle = res.title;
            activity.gradeSchema = GRADE_SCHEMA.GRADED.key;
            activity.questions = !!res.hasOwnProperty("questions") ? res.questions : [];
            setBlankRestriction(activity.questions);
            setEditorView(ACTIVITY_EDITOR_VIEW.QUESTION_EDITING);
            setSavedActivity(activity);
            setDraftActivity(JSON.parse(JSON.stringify(activity)));
        }).finally(() => setDataLoading(false));

        // getQuestionProfile(questionProfileId).then(res => {
        //     for (const question of res.questions) {
        //         if (question.questionType === QUESTION_TYPES.MH.name && !question.correctAnswers) {
        //             question.correctAnswers = question.optionsMap;
        //         }
        //     }
        //
        //     let activity = {};
        //     activity.pollTitle = res.title;
        //     activity.gradeSchema = GRADE_SCHEMA.GRADED.key;
        //     activity.questions = !!res.hasOwnProperty("questions") ? res.questions : [];
        //     setBlankRestriction(activity.questions);
        //     setEditorView(ACTIVITY_EDITOR_VIEW.QUESTION_EDITING);
        //     setSavedActivity(activity);
        //     setDraftActivity(JSON.parse(JSON.stringify(activity)));
        // }).finally(() => setDataLoading(false));
    }, []);

    useEffect(() => {
        return () => {
            window.onbeforeunload = undefined;
        }
    }, []);


    const deletePollConfig = {
        title: t("questionBank.deleteQuestionBankTitle"),
        okText: t("common.delete"),
        cancelText: t("common.cancel"),
        submit: () => {
            deleteQuestionProfile(questionProfileId).then(() => {
                history.push(MENU_LIST.QUESTION_BANKS.path);
            });
        },
        close: () => {
            setShowDeleteModal(false);
        },
    };

    if (editorView === ACTIVITY_EDITOR_VIEW.IMPORT_QUESTIONS) {
        return (
            <ActivityEditorImportQuestions
                setEditorView={setEditorView}
                draftActivity={draftActivity}
                setDraftActivity={setDraftActivity}
                courseList={courseList}
                userSettings={userSettings}
                questionBankId={questionProfileId}
            />
        );
    }



    return (
        <>
            {leaveConfirmationModal}
            <YuJaGeneralAlert
                show={showDeleteModal}
                setModalShow={setShowDeleteModal}
                content={t("questionBank.deleteQuestionBankModalBody")}
                config={deletePollConfig}
                AlertType={t("common.delete")}
            />

            <div className={`${classes.parentContainer} ${classes.gridLayout}`}>
                <ActivityEditorTitleContainer
                    generateTitle={generateDefaultTitle}
                    dataLoading={dataLoading}
                    draftActivity={draftActivity}
                    setDraftActivity={setDraftActivity}
                    userSettings={userSettings}
                    currQueDisplay={currQueDisplay}
                    setCurrQueDisplay={setCurrQueDisplay}
                    isAutoSaving={isAutoSaving}
                    isSaving={isSaving}
                    handleSave={editQueBankClose}
                    setShowDeleteModal={setShowDeleteModal}
                    questionBankId={questionProfileId}
                    saveBtnDisabled={isSaving || isAutoSaving}
                    editorView={editorView}
                    setEditorView={setEditorView}
                />
                <ActivityEditorDashboard
                    dataLoading={dataLoading}
                    editorView={editorView}
                    setEditorView={setEditorView}
                    draftActivity={draftActivity}
                    setQuestions={setQuestions}
                    currQueDisplay={currQueDisplay}
                    setCurrQueDisplay={setCurrQueDisplay}
                    userSettings={userSettings}
                    questionBankId={questionProfileId}
                />
                <ActivityEditorMainView
                    userSettings={userSettings}
                    dataLoading={dataLoading}
                    editorView={editorView}
                    setEditorView={setEditorView}
                    draftActivity={draftActivity}
                    setDraftActivity={setDraftActivity}
                    currQueDisplay={currQueDisplay}
                    setCurrQueDisplay={setCurrQueDisplay}
                    questionBankId={questionProfileId}
                    savePoll={editQueBank}
                />
            </div>
        </>
    );
}